import { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Tabs, TabPanel, useTabs } from '@/components/ui/Tabs';
import {
  useCapacityOverview,
  useCapacityForecast,
  useRebalanceSuggestions,
  useApplyRebalanceSuggestion,
  useTeams,
  type ForecastWeek,
  type RebalanceSuggestion,
} from './hooks/useCapacity';
import { WeeklyResourcePlanner } from './WeeklyResourcePlanner';

// ─── Constants ──────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'planner', label: 'Resource Planner' },
  { id: 'forecast', label: 'Workload Forecast' },
  { id: 'rebalance', label: 'Rebalance Suggestions' },
];

// ─── Main Component ─────────────────────────────────────────────────────────

/**
 * CapacityDashboard — Staff capacity overview, workload forecast, and rebalance suggestions.
 * Requirements: 18.1, 18.2, 18.3
 */
export function CapacityDashboard() {
  const { activeTab, onChange } = useTabs('overview');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedWeeks, setSelectedWeeks] = useState(4);
  const { data: teams } = useTeams();

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Capacity &amp; Workload
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Monitor staff availability, forecast workload, and review rebalancing recommendations.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 dark:text-gray-400">Weeks:</label>
            <select
              value={selectedWeeks}
              onChange={(e) => setSelectedWeeks(Number(e.target.value))}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              aria-label="Number of weeks to display"
            >
              <option value={2}>2 weeks</option>
              <option value={4}>4 weeks</option>
              <option value={6}>6 weeks</option>
              <option value={8}>8 weeks</option>
              <option value={12}>12 weeks</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 dark:text-gray-400">Team:</label>
            <select
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              <option value="">All Teams</option>
              {(teams ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <OverviewCards teamId={selectedTeamId || undefined} weeks={selectedWeeks} />

      {/* Tabs */}
      <Tabs tabs={TABS} activeTab={activeTab} onChange={onChange} />

      <TabPanel tabId="overview" activeTab={activeTab}>
        <StaffAvailabilityGrid teamId={selectedTeamId || undefined} weeks={selectedWeeks} />
      </TabPanel>

      <TabPanel tabId="planner" activeTab={activeTab}>
        <WeeklyResourcePlanner teamId={selectedTeamId || undefined} weeks={selectedWeeks} />
      </TabPanel>

      <TabPanel tabId="forecast" activeTab={activeTab}>
        <WorkloadForecastChart teamId={selectedTeamId || undefined} weeks={selectedWeeks} />
      </TabPanel>

      <TabPanel tabId="rebalance" activeTab={activeTab}>
        <RebalanceSuggestionsPanel teamId={selectedTeamId || undefined} weeks={selectedWeeks} />
      </TabPanel>
    </div>
  );
}

// ─── Overview Cards ─────────────────────────────────────────────────────────

function OverviewCards({ teamId, weeks }: { teamId?: string; weeks: number }) {
  const { data: overview, isLoading: overviewLoading } = useCapacityOverview(teamId);
  const { data: forecast, isLoading: forecastLoading } = useCapacityForecast({ weeks, teamId });

  if (overviewLoading || forecastLoading) {
    return <OverviewCardsSkeleton />;
  }

  if (!overview) {
    return (
      <div className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
        Unable to load capacity overview.
      </div>
    );
  }

  // Derive totals from the forecast weekly data to stay consistent with the calendar.
  // The forecast endpoint already applies team filtering and Monday-aligned week matching.
  const forecastAvailable =
    forecast?.weeks.reduce((sum, w) => sum + w.availableHours, 0) ?? overview.totalAvailableHours;
  const forecastAssigned =
    forecast?.weeks.reduce((sum, w) => sum + w.assignedHours, 0) ?? overview.totalAssignedHours;
  const utilization = forecastAvailable > 0 ? (forecastAssigned / forecastAvailable) * 100 : 0;
  const utilizationColor = getUtilizationColor(utilization);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        label="Available Hours"
        value={`${forecastAvailable.toLocaleString('en-CA')}h`}
        description="Total this period"
      />
      <MetricCard
        label="Assigned Hours"
        value={`${forecastAssigned.toLocaleString('en-CA')}h`}
        description="Scheduled workload"
      />
      <MetricCard
        label="Utilization"
        value={`${utilization.toFixed(1)}%`}
        description="Assigned / Available"
        valueClassName={utilizationColor}
      />
      <MetricCard
        label="Staff"
        value={`${overview.staffCount}`}
        description={
          <span className="flex items-center gap-2">
            {overview.overUtilizedCount > 0 && (
              <Badge variant="danger" size="sm">
                {overview.overUtilizedCount} over
              </Badge>
            )}
            {overview.underUtilizedCount > 0 && (
              <Badge variant="warning" size="sm">
                {overview.underUtilizedCount} under
              </Badge>
            )}
            {overview.overUtilizedCount === 0 && overview.underUtilizedCount === 0 && (
              <Badge variant="success" size="sm">
                balanced
              </Badge>
            )}
          </span>
        }
      />
    </div>
  );
}

function OverviewCardsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-busy="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i}>
          <CardContent>
            <div className="animate-pulse space-y-2">
              <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-8 w-16 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-3 w-28 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Staff Availability Grid ────────────────────────────────────────────────

function StaffAvailabilityGrid({ teamId, weeks }: { teamId?: string; weeks: number }) {
  const { data: forecast, isLoading } = useCapacityForecast({ weeks, teamId });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" aria-live="polite">
        <LoadingSpinner />
        <span className="ml-2 text-sm text-gray-500">Loading staff availability...</span>
      </div>
    );
  }

  if (!forecast || forecast.weeks.length === 0) {
    return (
      <Card>
        <CardContent>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            No availability data for the selected period.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Staff Availability Calendar"
        description="Weekly view of available vs assigned hours across the team"
      />
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" aria-label="Staff availability by week">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="pb-3 text-left font-medium text-gray-500 dark:text-gray-400">
                  Week
                </th>
                <th className="pb-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Available
                </th>
                <th className="pb-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Assigned
                </th>
                <th className="pb-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Utilization
                </th>
                <th className="pb-3 text-left font-medium text-gray-500 dark:text-gray-400">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {forecast.weeks.map((week) => (
                <AvailabilityWeekRow key={week.weekStart} week={week} />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function AvailabilityWeekRow({ week }: { week: ForecastWeek }) {
  const utilColor = getUtilizationColor(week.utilizationPercent);
  const statusBadge = getUtilizationBadge(week.utilizationPercent);

  return (
    <tr>
      <td className="py-3 font-medium text-gray-900 dark:text-gray-100">
        {formatWeekLabel(week.weekStart, week.weekEnd)}
      </td>
      <td className="py-3 text-right text-gray-700 dark:text-gray-300">
        {week.availableHours.toFixed(1)}h
      </td>
      <td className="py-3 text-right text-gray-700 dark:text-gray-300">
        {week.assignedHours.toFixed(1)}h
      </td>
      <td className={`py-3 text-right font-semibold ${utilColor}`}>
        {week.utilizationPercent.toFixed(1)}%
      </td>
      <td className="py-3">
        <Badge variant={statusBadge.variant} size="sm" dot>
          {statusBadge.label}
        </Badge>
      </td>
    </tr>
  );
}

// ─── Workload Forecast Chart ────────────────────────────────────────────────

function WorkloadForecastChart({ teamId, weeks }: { teamId?: string; weeks: number }) {
  const { data: forecast, isLoading } = useCapacityForecast({ weeks, teamId });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" aria-live="polite">
        <LoadingSpinner />
        <span className="ml-2 text-sm text-gray-500">Loading forecast...</span>
      </div>
    );
  }

  if (!forecast || forecast.weeks.length === 0) {
    return (
      <Card>
        <CardContent>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            No forecast data available.
          </p>
        </CardContent>
      </Card>
    );
  }

  const maxHours = Math.max(
    ...forecast.weeks.map((w) => Math.max(w.availableHours, w.assignedHours)),
    1,
  );

  return (
    <Card>
      <CardHeader title="Workload Forecast" description="Available vs assigned hours per week" />
      <CardContent>
        {/* Chart Legend */}
        <div className="mb-4 flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded bg-blue-500" aria-hidden="true" />
            <span className="text-gray-600 dark:text-gray-400">Available</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded bg-amber-500" aria-hidden="true" />
            <span className="text-gray-600 dark:text-gray-400">Assigned</span>
          </div>
        </div>

        {/* Bar Chart */}
        <div
          className="space-y-3"
          role="img"
          aria-label={`Workload forecast for ${forecast.weeks.length} weeks`}
        >
          {forecast.weeks.map((week) => (
            <ForecastBar key={week.weekStart} week={week} maxHours={maxHours} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ForecastBar({ week, maxHours }: { week: ForecastWeek; maxHours: number }) {
  const availablePercent = (week.availableHours / maxHours) * 100;
  const assignedPercent = (week.assignedHours / maxHours) * 100;
  const isOverloaded = week.assignedHours > week.availableHours;

  return (
    <div className="group">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-medium text-gray-700 dark:text-gray-300">
          {formatShortWeek(week.weekStart)}
        </span>
        <span
          className={
            isOverloaded
              ? 'font-semibold text-red-600 dark:text-red-400'
              : 'text-gray-500 dark:text-gray-400'
          }
        >
          {week.assignedHours.toFixed(0)}h / {week.availableHours.toFixed(0)}h
        </span>
      </div>
      <div className="relative h-5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        {/* Available bar (background) */}
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-blue-200 dark:bg-blue-900/40"
          style={{ width: `${Math.max(availablePercent, 1)}%` }}
        />
        {/* Assigned bar (foreground) */}
        <div
          className={`absolute inset-y-0 left-0 rounded-full transition-all ${
            isOverloaded ? 'bg-red-500 dark:bg-red-600' : 'bg-amber-400 dark:bg-amber-500'
          }`}
          style={{ width: `${Math.max(assignedPercent, 1)}%` }}
        />
      </div>
    </div>
  );
}

// ─── Rebalance Suggestions Panel ────────────────────────────────────────────

function RebalanceSuggestionsPanel({ teamId, weeks }: { teamId?: string; weeks: number }) {
  const { data: suggestions, isLoading } = useRebalanceSuggestions(teamId, weeks);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" aria-live="polite">
        <LoadingSpinner />
        <span className="ml-2 text-sm text-gray-500">Loading suggestions...</span>
      </div>
    );
  }

  if (!suggestions || suggestions.length === 0) {
    return (
      <Card>
        <CardHeader title="Rebalance Suggestions" />
        <CardContent>
          <div className="flex flex-col items-center py-8 text-center">
            <svg
              className="h-12 w-12 text-gray-300 dark:text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              No rebalancing suggestions at this time. Workload appears balanced.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const pendingSuggestions = suggestions.filter((s) => s.status === 'pending');
  const appliedSuggestions = suggestions.filter((s) => s.status === 'applied');

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Rebalance Suggestions"
          description={`${pendingSuggestions.length} pending recommendation${pendingSuggestions.length !== 1 ? 's' : ''}`}
        />
        <CardContent>
          <div className="space-y-3">
            {pendingSuggestions.map((suggestion) => (
              <SuggestionCard key={suggestion.id} suggestion={suggestion} />
            ))}
            {pendingSuggestions.length === 0 && (
              <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                All suggestions have been addressed.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {appliedSuggestions.length > 0 && (
        <Card>
          <CardHeader
            title="Recently Applied"
            description={`${appliedSuggestions.length} suggestion${appliedSuggestions.length !== 1 ? 's' : ''} applied`}
          />
          <CardContent>
            <div className="space-y-2">
              {appliedSuggestions.slice(0, 5).map((suggestion) => (
                <div
                  key={suggestion.id}
                  className="flex items-center justify-between rounded-lg border border-gray-100 px-4 py-2 dark:border-gray-700"
                >
                  <div className="text-sm">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {suggestion.jobTitle}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {' '}
                      — moved from {suggestion.fromUserName} to {suggestion.toUserName}
                    </span>
                  </div>
                  <Badge variant="success" size="sm">
                    Applied
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SuggestionCard({ suggestion }: { suggestion: RebalanceSuggestion }) {
  const applyMutation = useApplyRebalanceSuggestion();

  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-gray-900 dark:text-gray-100">{suggestion.jobTitle}</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Reassign from{' '}
            <span className="font-medium text-gray-800 dark:text-gray-200">
              {suggestion.fromUserName}
            </span>{' '}
            to{' '}
            <span className="font-medium text-gray-800 dark:text-gray-200">
              {suggestion.toUserName}
            </span>
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant="outline" size="sm">
              {suggestion.estimatedHours}h estimated
            </Badge>
            <span className="text-xs text-gray-500 dark:text-gray-400">{suggestion.reason}</span>
          </div>
          {suggestion.impact && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Impact: {suggestion.impact}
            </p>
          )}
        </div>
        <Button
          size="sm"
          variant="primary"
          onClick={() =>
            applyMutation.mutate({
              suggestionId: suggestion.id,
              jobId: suggestion.jobId,
              toUserId: suggestion.toUserId,
            })
          }
          loading={applyMutation.isPending}
          disabled={applyMutation.isPending}
          aria-label={`Apply suggestion to reassign ${suggestion.jobTitle}`}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}

// ─── Shared Components ──────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string;
  description: string | React.ReactNode;
  valueClassName?: string;
}

function MetricCard({ label, value, description, valueClassName }: MetricCardProps) {
  return (
    <Card>
      <CardContent>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <p
          className={`mt-1 text-2xl font-bold ${valueClassName || 'text-gray-900 dark:text-gray-100'}`}
        >
          {value}
        </p>
        <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</div>
      </CardContent>
    </Card>
  );
}

function LoadingSpinner() {
  return (
    <svg
      className="h-5 w-5 animate-spin text-brand-500"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getUtilizationColor(percent: number): string {
  if (percent >= 100) return 'text-red-600 dark:text-red-400';
  if (percent >= 85) return 'text-amber-600 dark:text-amber-400';
  if (percent >= 60) return 'text-green-600 dark:text-green-400';
  return 'text-blue-600 dark:text-blue-400';
}

function getUtilizationBadge(percent: number): {
  variant: 'success' | 'warning' | 'danger' | 'info';
  label: string;
} {
  if (percent >= 100) return { variant: 'danger', label: 'Over-utilized' };
  if (percent >= 85) return { variant: 'warning', label: 'Near capacity' };
  if (percent >= 60) return { variant: 'success', label: 'Optimal' };
  return { variant: 'info', label: 'Under-utilized' };
}

function formatWeekLabel(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${startDate.toLocaleDateString('en-CA', opts)} – ${endDate.toLocaleDateString('en-CA', opts)}`;
}

function formatShortWeek(start: string): string {
  const date = new Date(start);
  return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}
